import { useEffect, useRef, useState } from "react";
import { useAppState } from "../context/AppState";
import { api } from "../lib/api";

const PROMO_MEDIA_CACHE_VERSION = "20260810-horizontal-ratio-1";

function versionedMediaUrl(value) {
  const url = String(value || "").trim();
  if (!url) return "";
  return `${url}${url.includes("?") ? "&" : "?"}v=${PROMO_MEDIA_CACHE_VERSION}`;
}

export function normalizeBanners(payload, group = "main") {
  const slides = group === "other"
    ? (Array.isArray(payload?.otherSlides) ? payload.otherSlides : [])
    : (Array.isArray(payload?.mainSlides) ? payload.mainSlides : []);
  return normalizeSlides(slides);
}

function normalizeSlides(slides) {
  return slides
    .map((slide) => ({
      image: versionedMediaUrl(slide?.data?.image),
      title: String(slide?.data?.name || "Promoción").trim(),
      url: String(slide?.url || "").trim(),
      mediaType: String(slide?.data?.media_type || "IMAGE").toUpperCase(),
      size: Number(slide?.promo_slider?.size || 0),
      aspectRatio: Number(slide?.data?.aspect_ratio || 0),
    }))
    .filter((banner) => banner.image);
}

export function normalizeSliderGroups(payload) {
  if (Array.isArray(payload?.sliderGroups)) {
    return payload.sliderGroups
      .map((group, index) => ({
        id: Number(group?.id || index + 1),
        name: String(group?.name || "Promociones"),
        position: Math.max(0, Number(group?.position_id || 0)),
        size: Number(group?.size || 0),
        items: normalizeSlides(Array.isArray(group?.slides) ? group.slides : []),
      }))
      .filter((group) => group.items.length > 0);
  }

  const mainItems = normalizeBanners(payload, "main");
  const otherItems = normalizeBanners(payload, "other");
  const groups = [];
  if (mainItems.length) {
    groups.push({ id: "legacy-main", name: "Promociones", position: 0, size: 0, items: mainItems });
  }
  if (otherItems.length) {
    groups.push({
      id: "legacy-other",
      name: "Promociones",
      position: Math.max(1, Number(payload?.otherSlides?.[0]?.promo_slider?.position_id || 1)),
      size: 0,
      items: otherItems,
    });
  }
  return groups;
}

export default function PromoBanner({
  className = "",
  items = null,
  preserveAspectRatio = true,
  minimumAspectRatio = 0,
}) {
  const { address } = useAppState();
  const [loadedBanners, setLoadedBanners] = useState([]);
  const [activeBanner, setActiveBanner] = useState(0);
  const [mediaAspectRatios, setMediaAspectRatios] = useState({});
  const [preloadNext, setPreloadNext] = useState(false);
  const touchStartX = useRef(null);
  const banners = Array.isArray(items) ? items : loadedBanners;
  const activeMediaKey = banners[activeBanner]?.image || "";
  const naturalAspectRatio = mediaAspectRatios[activeMediaKey]
    || banners[activeBanner]?.aspectRatio
    || null;

  useEffect(() => {
    if (Array.isArray(items)) return undefined;
    let mounted = true;
    api("/promo-slider", {
      method: "POST",
      body: JSON.stringify({
        latitude: address?.lat ?? null,
        longitude: address?.lng ?? null,
      }),
    })
      .then((response) => {
        if (!mounted) return;
        setLoadedBanners(normalizeBanners(response));
        setActiveBanner(0);
      })
      .catch(() => mounted && setLoadedBanners([]));
    return () => { mounted = false; };
  }, [address?.lat, address?.lng, items]);

  useEffect(() => {
    setActiveBanner(0);
    setMediaAspectRatios({});
    setPreloadNext(false);
  }, [items]);

  useEffect(() => {
    setPreloadNext(false);
    if (banners.length < 2) return undefined;
    const timer = window.setTimeout(() => setPreloadNext(true), 1600);
    return () => window.clearTimeout(timer);
  }, [activeBanner, banners.length]);

  useEffect(() => {
    if (banners.length < 2) return undefined;
    const timer = window.setInterval(
      () => setActiveBanner((current) => (current + 1) % banners.length),
      5500,
    );
    return () => window.clearInterval(timer);
  }, [banners.length]);

  const showBanner = (index) => {
    if (!banners.length) return;
    setActiveBanner((index + banners.length) % banners.length);
  };

  if (!banners.length) return null;

  return (
    <section
      className={`banner promo-banner ${className}`.trim()}
      style={preserveAspectRatio && naturalAspectRatio
        ? { aspectRatio: String(naturalAspectRatio) }
        : undefined}
      aria-label="Promociones"
      onTouchStart={(event) => {
        touchStartX.current = event.touches?.[0]?.clientX ?? null;
      }}
      onTouchEnd={(event) => {
        const endX = event.changedTouches?.[0]?.clientX ?? null;
        if (
          touchStartX.current !== null
          && endX !== null
          && Math.abs(endX - touchStartX.current) > 45
        ) {
          showBanner(activeBanner + (endX < touchStartX.current ? 1 : -1));
        }
        touchStartX.current = null;
      }}
      onTouchCancel={() => { touchStartX.current = null; }}
    >
      <div
        className="banner__track"
        style={{ transform: `translate3d(-${activeBanner * 100}%, 0, 0)` }}
      >
        {banners.map((banner, index) => {
          const active = index === activeBanner;
          const next = index === ((activeBanner + 1) % banners.length);
          const mediaReady = active || Boolean(mediaAspectRatios[banner.image]) || (preloadNext && next);
          return (
            <a
              className="banner__slide"
              href={banner.url || undefined}
              key={`${banner.image}-${index}`}
              aria-hidden={!active}
              tabIndex={active ? 0 : -1}
              onClick={(event) => {
                if (!banner.url) event.preventDefault();
              }}
            >
              {banner.mediaType === "VIDEO" ? (
                active ? (
                  <video
                    src={banner.image}
                    muted
                    playsInline
                    autoPlay
                    loop
                    preload="metadata"
                    onLoadedMetadata={(event) => {
                      if (!preserveAspectRatio) return;
                      const { videoWidth, videoHeight } = event.currentTarget;
                      if (videoWidth > 0 && videoHeight > 0) {
                        const ratio = Math.max(videoWidth / videoHeight, minimumAspectRatio);
                        setMediaAspectRatios((current) => ({ ...current, [banner.image]: ratio }));
                      }
                    }}
                  />
                ) : null
              ) : mediaReady ? (
                <img
                  src={banner.image}
                  alt={banner.title}
                  decoding="async"
                  loading={active ? "eager" : "lazy"}
                  fetchPriority={active ? "high" : "low"}
                  onLoad={(event) => {
                    if (!preserveAspectRatio) return;
                    const { naturalWidth, naturalHeight } = event.currentTarget;
                    if (naturalWidth > 0 && naturalHeight > 0) {
                      const ratio = Math.max(naturalWidth / naturalHeight, minimumAspectRatio);
                      setMediaAspectRatios((current) => ({ ...current, [banner.image]: ratio }));
                    }
                  }}
                />
              ) : null}
            </a>
          );
        })}
      </div>
      {banners.length > 1 && (
        <div className="banner__dots" aria-label="Seleccionar banner">
          {banners.map((_, index) => (
            <button
              aria-label={`Mostrar promoción ${index + 1}`}
              className={index === activeBanner ? "is-active" : ""}
              key={index}
              onClick={() => showBanner(index)}
              type="button"
            />
          ))}
        </div>
      )}
    </section>
  );
}
